/**
 * "Mark times proposed" modal body.
 *
 * Two things a recruiter really does when proposing interview slots:
 *  1. offer SEVERAL slots at once (candidates get a choice) → checkbox list of
 *     the graded slots + a free-text "Custom…" option, at least one required;
 *  2. how the candidate was told. **Email is the default** (Theresa, Sep 2026):
 *     it is what recruiters actually do, it leaves the candidate something to
 *     reply to, and the reply is what the Requests tab picks up. The email path
 *     reveals an editable draft naming the chosen slots, in DE or EN, and SENDS
 *     it from the recruiter's own Gmail behind the board's 10-second undo. Copy /
 *     mail-app appear only when the board cannot send (see SendFallback). Phone
 *     stays, for the calls that do still happen.
 *
 * Confirming only writes our own tracking entry (slot labels + channel note);
 * the Lever stage is untouched.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { RadioGroup, RadioItem } from '@finn-auto/design-system/atoms/radio';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import type { GradedSlot, PanelConfig, RoleBoard, SchedulingCandidate } from '../../types/taActionBoard';
import { resolveEmailTemplate } from '../../services/emailTemplatesApi';
import { EmailLibraryLink } from './EmailLibraryLink';
import {
  fmtSlotRange,
  fmtSlotRangeDe,
  proposableSlots,
  slotGradeHint,
  slotNumber,
  slotOffersStart,
} from './format';
import { buildOfferEmailBody } from './modalDrafts';
import { ModalActions } from './ModalActions';
import { LanguageToggle, type DraftLanguage } from './LanguageToggle';
import { SendFallback } from './SendFallback';
import { SendOptIn } from './SendOptIn';
import type { BryqKey } from '../../services/bryqApi';
import { BryqAlongsideField } from './BryqAlongsideField';
import { useBryqNote, withBryqNote } from './bryqNote';

/** How the recruiter reached (or failed to reach) the candidate. */
export type OfferChannel = 'phone' | 'email';

/** The slot-proposal email to send, with the role's case material attached. */
export interface OfferedEmail {
  subject: string;
  body: string;
  material: PanelConfig['caseMaterial'];
  /** "Also send a Bryq test": sent by the server right after this email went out. */
  bryq?: BryqKey | null;
}

/**
 * Last-resort subject, per language.
 *
 * Normally the Email Library supplies it (`invite/slot-proposal`) and this is
 * never seen. It was a single German string, so the one case that reaches it —
 * the Library read failing, or the template being deleted — put a German subject
 * on an English draft whose BODY had correctly fallen back to English.
 */
const EMAIL_SUBJECT: Record<DraftLanguage, string> = {
  de: 'Terminvorschlag für dein Interview bei FINN',
  en: 'Proposed times for your interview at FINN',
};

export function OfferedBody({
  role,
  candidate,
  senderFirstName,
  gmailConnected,
  onConfirm,
  onCancel,
  slotsOverride,
  preselected,
  titlePrefix = 'Propose times',
  confirmVerb = 'Mark proposed',
}: {
  role: RoleBoard;
  candidate: SchedulingCandidate;
  /** First name of the signed-in recruiter — signs the drafted email */
  senderFirstName: string;
  /** A Google account is connected on the board — sending is possible. */
  gmailConnected: boolean;
  /**
   * `email` non-null = also send it as the recruiter, behind the undo countdown.
   * `starts` are the picked slots' ISO starts — the labels are prose, and the
   * board needs the times themselves to stamp WHICH held slot went to whom.
   */
  onConfirm: (
    slotLabels: string[],
    channel: OfferChannel,
    email: OfferedEmail | null,
    starts: string[],
    /** Per-graded-slot {label, grade} for the picked options — badges the proposed times later. */
    slotMeta: { label: string; grade: string; start?: string }[]
  ) => void;
  onCancel: () => void;
  /**
   * Final Rounds reuses this body to propose final-round options: same
   * multi-select + phone/email flow, different slot source. Chains map onto
   * the GradedSlot shape (start/end/grade/conflicts).
   */
  slotsOverride?: GradedSlot[];
  /**
   * ISO starts to open with ticked — the options the recruiter already chose in
   * the section above. Without it the modal defaults to the first slot, which
   * silently discards that choice.
   */
  preselected?: string[];
  titlePrefix?: string;
  confirmVerb?: string;
}) {
  /**
   * The slots on offer. `proposableSlots` puts EVERY ticked slot in the list
   * before filling up with suggestions — the old `topSlots(slots)` capped it at
   * three chosen without reference to the selection, so a ticked slot could be
   * absent from the modal entirely and the candidate got times nobody picked.
   */
  const slots = slotsOverride ?? proposableSlots(role.scheduling.slots, preselected || []);
  // multi-select: several slots can be offered at once (keyed by slot start)
  const [picked, setPicked] = useState<string[]>(() => {
    // Matched through `slotOffersStart`, because a start ticked INSIDE a grouped
    // free window is not that window's own `start` and used to fall through
    // this filter — silently discarding the recruiter's choice.
    const carried = slots.filter((s) => (preselected || []).some((start) => slotOffersStart(s, start)));
    if (carried.length > 0) return carried.map((s) => s.start);
    return slots.length > 0 ? [slots[0].start] : [];
  });
  const [customOn, setCustomOn] = useState(slots.length === 0);
  const [custom, setCustom] = useState('');
  // Email by default: proposing times in writing is the normal path, and it is the one that
  // leaves a thread for the candidate's reply to land in (Theresa, Sep 2026).
  const [channel, setChannel] = useState<OfferChannel>('email');
  // The role's stored case material rides along automatically — that is the
  // whole point of storing it per role rather than picking it every time.
  const material = role.scheduling.panel?.caseMaterial ?? null;
  // For a take-home case (the role has case material to submit) tell the candidate
  // where to send it: the case panel. Only on the case proposal, never the
  // final-round reuse (`slotsOverride`). Case slots are graded panel-style, so the
  // interviewers are the same for every proposed slot — the role panel itself.
  const caseSubmissionEmails = (role.scheduling.panel?.interviewers ?? []).join(', ');
  const showCaseSubmission = !slotsOverride && !!material && caseSubmissionEmails.length > 0;
  const canSend = gmailConnected && !!candidate.email;
  // Armed by default when sending is possible — see MoveBody for why.
  const [sendEnabled, setSendEnabled] = useState(canSend);
  /**
   * The ONE thing that decides whether a candidate is emailed. `sendEnabled` is
   * deliberately NOT cleared when the recruiter switches to phone — switching
   * back to email should restore what they chose — so the channel has to be
   * part of the answer here.
   *
   * Passing `sendEnabled` through on its own is what emailed a candidate the
   * recruiter had just spoken to (Theresa, Sep 2026): the opt-in arms itself,
   * the checkbox that would clear it lives INSIDE the email branch and is not
   * rendered on the phone one, so picking "Told them by phone" hid the control
   * without disarming it. The board then wrote "Reached by phone" on a
   * proposal it had just emailed.
   */
  const sendNow = channel === 'email' && sendEnabled;
  /**
   * DE / EN. The Email Library holds both, so switching re-resolves the
   * template rather than translating anything on the client: a rendered
   * translation would be words TA never approved.
   *
   * Opens on the ROLE's own language (the posting's tags), so an English
   * pipeline does not cost a click every time. It was a hard-coded 'de' until
   * Sep 2026, which made every English case proposal a two-step job.
   */
  const [language, setLanguage] = useState<DraftLanguage>(role.language ?? 'de');
  const [bryq, setBryq] = useState<BryqKey | null>(null);

  const pickedSlots: GradedSlot[] = slots.filter((s) => picked.includes(s.start));
  const customLabel = custom.trim();
  const slotLabels = [
    ...pickedSlots.map(fmtSlotRange),
    ...(customOn && customLabel ? [customLabel] : []),
  ];
  /**
   * The times as the CANDIDATE reads them, formatted in the draft's own
   * language. German bullets under English prose was the one thing a language
   * toggle must not leave behind.
   */
  const slotLines = [
    ...pickedSlots.map(language === 'de' ? fmtSlotRangeDe : fmtSlotRange),
    ...(customOn && customLabel ? [customLabel] : []),
  ];

  const candidateFirstName = candidate.name.trim().split(/\s+/)[0] || candidate.name;
  // TA owns this wording in the Email Library (`invite/slot-proposal`). `{slots}` is deliberately
  // NOT passed to the server — /resolve leaves unknown tokens verbatim — and substituted locally,
  // so the draft follows every checkbox change without a round-trip. The in-code copy stays as the
  // fallback ONLY: "Mark offered" must never be blocked by a Firestore read (same trade as the
  // final-round summary; deleting the template degrades to the old copy, never to no email).
  const templateQuery = useQuery({
    queryKey: ['offer-template', candidate.opportunityId, candidate.stageName, senderFirstName, language],
    queryFn: () =>
      resolveEmailTemplate({
        category: 'invite',
        key: 'slot-proposal',
        lang: language,
        vars: {
          candidateFirstName,
          stageName: candidate.stageName,
          recruiterName: senderFirstName,
        },
      }),
    retry: false,
    staleTime: 60_000,
  });
  // The take-home submission address, wording owned by TA (`booking-page/case-submission`).
  // {caseSubmissionEmails} is filled server-side from the vars we pass. Only fetched for a
  // take-home case; a failed read appends nothing (never blocks the draft).
  const caseSubmissionQuery = useQuery({
    queryKey: ['case-submission-snippet', language, caseSubmissionEmails],
    queryFn: () =>
      resolveEmailTemplate({
        category: 'booking-page',
        key: 'case-submission',
        lang: language,
        vars: { caseSubmissionEmails },
      }),
    enabled: showCaseSubmission,
    retry: false,
    staleTime: 60_000,
  });
  const bullets =
    slotLines.length > 0
      ? slotLines.map((l) => `- ${l}`).join('\n')
      : language === 'de'
        ? '- (Termin ergänzen)'
        : '- (add a time)';
  // The times must survive ANY template edit: if TA rewords slot-proposal and loses `{slots}`,
  // every offer email would draft as complete-looking prose with no times in it — so a template
  // without the token gets the bullets appended rather than silently dropped.
  const draftBody = templateQuery.data
    ? templateQuery.data.body.includes('{slots}')
      ? templateQuery.data.body.replaceAll('{slots}', bullets)
      : `${templateQuery.data.body}\n\n${bullets}`
    : buildOfferEmailBody({
        candidateFirstName,
        stageName: candidate.stageName,
        slotLines,
        senderFirstName,
        // A link rides in the TEXT; a stored file is attached instead, so it
        // must not also appear as a URL the candidate cannot open.
        materialLink:
          material?.kind === 'link' ? { url: material.url, label: material.label } : null,
        language,
      });
  // Append the take-home submission line to the draft, so it follows the selection
  // and stays editable. Empty for a non-case proposal or if the snippet did not load.
  const caseSubmissionLine = showCaseSubmission ? caseSubmissionQuery.data?.body?.trim() || '' : '';
  const draftBodyWithCase = caseSubmissionLine ? `${draftBody}\n\n${caseSubmissionLine}` : draftBody;
  // The draft follows the slot selection (and the template fetch) until edited by hand.
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const subject = subjectEdit ?? templateQuery.data?.subject ?? EMAIL_SUBJECT[language];
  const [bodyEdit, setBodyEdit] = useState<string | null>(null);
  // "Send Bryq in parallel": the paragraph follows the tick, in the draft and in an edited text.
  const bryqNote = useBryqNote(language);
  const body = bodyEdit ?? withBryqNote(draftBodyWithCase, bryqNote, bryq !== null);
  const onBryq = (next: BryqKey | null) => {
    setBryq(next);
    if (bodyEdit !== null) setBodyEdit(withBryqNote(bodyEdit, bryqNote, next !== null));
  };

  const toggle = (start: string) =>
    setPicked((prev) => (prev.includes(start) ? prev.filter((s) => s !== start) : [...prev, start]));

  return (
    <>
      <ModalHeader>
        <ModalTitle>
          {titlePrefix} — {candidate.name}
        </ModalTitle>
      </ModalHeader>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-gray-700">
            Proposed slots <span className="font-normal text-gray-400">(pick one or more)</span>
          </span>
          <div className="space-y-2 rounded-lg bg-gray-50 p-3">
            {slots.map((s, i) => (
              <Checkbox
                key={s.start}
                label={`${slotNumber(i)} ${fmtSlotRange(s)} — ${slotGradeHint(s)}`}
                labelClassName="text-sm text-gray-700"
                checked={picked.includes(s.start)}
                onCheckedChange={() => toggle(s.start)}
              />
            ))}
            <Checkbox
              label="Custom…"
              labelClassName="text-sm text-gray-700"
              checked={customOn}
              onCheckedChange={(v) => setCustomOn(v === true)}
            />
            {slots.length === 0 && (
              <p className="text-xs text-gray-500">
                No graded slot in the next 7 days — enter the slot you proposed below.
              </p>
            )}
          </div>
        </div>

        {/* Only rendered when "Custom…" is ticked. Static label ABOVE the field —
            the DS floating label sits over the placeholder text. */}
        {customOn && (
          <div className="space-y-1.5">
            <label htmlFor="ta-custom-slot" className="block text-sm font-medium text-gray-700">
              Custom slot
            </label>
            <Input
              id="ta-custom-slot"
              value={custom}
              onChange={(e) => setCustom((e.target as HTMLInputElement).value)}
              placeholder="e.g. Fri Aug 07 · 14:00–15:30"
            />
          </div>
        )}

        <RadioGroup
          label="How are they getting these times?"
          value={channel}
          onValueChange={(v: string) => setChannel(v as OfferChannel)}
        >
          <RadioItem value="email" label="Email them the times" />
          <RadioItem value="phone" label="Told them by phone" />
        </RadioGroup>

        {/* Says the quiet part out loud. The email branch is a visibly loaded
            form; this branch was blank, which is the same thing a silent send
            looks like. */}
        {channel === 'phone' && (
          <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
            <span className="font-semibold">No email goes to {candidate.name}.</span> Confirming
            only records the proposal on the board.
          </p>
        )}

        {channel === 'email' && (
          <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            <p className="text-sm text-gray-600">
              {canSend
                ? 'Send it from here, or copy it — confirming records the proposal in the board either way.'
                : 'Send this yourself (copy or mail app) — confirming only records the proposal in the board.'}
            </p>
            {material && (
              <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                <span className="font-semibold">
                  {material.kind === 'link' ? material.label : material.filename}
                </span>{' '}
                is this role's case material and goes out with the email.{' '}
                {material.kind === 'link'
                  ? 'It is a link in the text above, so copying the email carries it too.'
                  : !canSend && 'Copy and the mail app cannot carry it — only sending from here does.'}
              </p>
            )}
            <SendOptIn
              canSend={canSend}
              gmailConnected={gmailConnected}
              candidateEmail={candidate.email || ''}
              checked={sendEnabled}
              disabled={false}
              onChange={setSendEnabled}
            />
            <LanguageToggle
              value={language}
              onChange={setLanguage}
              hasEdits={subjectEdit !== null || bodyEdit !== null}
            />
            <Input
              label="Subject"
              value={subject}
              onChange={(e) => setSubjectEdit((e.target as HTMLInputElement).value)}
            />
            <Textarea
              label="Email draft"
              // DS Textarea maps only rows 2–5 to a height and silently falls back to the
              // 3-row height above that, collapsing the box under its own label.
              rows={5}
              textAreaClassName="min-h-[16rem]"
              value={body}
              onChange={(e) => setBodyEdit(e.target.value)}
            />
            {/* Only when the Library copy resolved — the in-code fallback has nothing to edit there. */}
            <div className="mt-1 text-right">
              <EmailLibraryLink templateKey={templateQuery.data ? 'slot-proposal' : null} />
            </div>
            {sendNow && (
              <BryqAlongsideField value={bryq} onChange={onBryq} language={language} opportunityId={candidate.opportunityId} />
            )}
            {/* Only when the board genuinely cannot send — see SendFallback. */}
            {!canSend && (
              <SendFallback
                subject={subject}
                body={body}
                candidateEmail={candidate.email}
                gmailConnected={gmailConnected}
                hasAttachment={!!material}
              />
            )}
          </div>
        )}
      </div>
      <ModalActions
        confirmLabel={channel === 'email' ? `${confirmVerb} by email` : confirmVerb}
        confirmDisabled={slotLabels.length === 0}
        onConfirm={() =>
          onConfirm(
            slotLabels,
            channel,
            sendNow ? { subject, body, material, bryq } : null,
            pickedSlots.map((s) => s.start),
            // `start` rides along so the time can be BOOKED later, not just
            // displayed — see taTracking.appendTrackingEntry.
            pickedSlots.map((s) => ({ label: fmtSlotRange(s), grade: s.grade, start: s.start }))
          )
        }
        onCancel={onCancel}
      />
    </>
  );
}
